import { FC } from 'react';
import { SimpleExpression, SimpleBinaryExpression, ExpressionItem, LogicalOperator, FieldMetadata, OperatorInfo } from '../../models/waterfall';
import { SimpleExpressionForm } from './SimpleExpressionForm';
import { LogicalOperatorToggle } from './LogicalOperatorToggle';
import { Button } from 'primereact/button';
import styles from './RuleGroup.module.css';

interface RuleGroupProps {
  group: SimpleExpression;
  level: number;
  onUpdate: (group: SimpleExpression) => void;
  fieldMetadata?: FieldMetadata[];
  operators?: OperatorInfo[];
}

export const RuleGroup: FC<RuleGroupProps> = ({ group, level, onUpdate, fieldMetadata, operators }) => {
  const expressions = group.expressions || [];
  const hasMultipleExpressions = expressions.length > 1;

  const hasIncompleteExpression =
    expressions.length === 0 ||
    expressions.some(
      (item) =>
        !item.expressionGroup &&
        (!item.expression || !item.expression.left || !item.expression.operator || !item.expression.right)
    );

  const handleLogicalOperatorChange = (value: LogicalOperator) => {
    onUpdate({
      ...group,
      logicalOperator: value,
    });
  };

  const handleUpdateExpression = (index: number, expression: SimpleBinaryExpression | null) => {
    console.log('[RuleGroup] handleUpdateExpression called:', { index, expression, currentExpressions: expressions });

    if (!expression) {
      console.log('[RuleGroup] Expression is null (incomplete form), preserving existing expression');
      return;
    }
    
    const updatedExpressions = [...expressions];
    
    // Check if the expression actually changed to avoid unnecessary updates
    const existingExpression = updatedExpressions[index]?.expression;
    if (
      existingExpression &&
      existingExpression.left === expression.left &&
      existingExpression.operator === expression.operator &&
      JSON.stringify(existingExpression.right) === JSON.stringify(expression.right)
    ) {
      console.log('[RuleGroup] No change detected, skipping update');
      return; // No change, don't update
    }
    
    if (index >= updatedExpressions.length) {
      console.log('[RuleGroup] Creating new expression at index:', index);
      updatedExpressions.push({
        expression,
        expressionGroup: null,
      });
    } else {
      console.log('[RuleGroup] Updating existing expression at index:', index);
      updatedExpressions[index] = {
        ...updatedExpressions[index],
        expression,
      };
    }

    const updatedGroup = {
      ...group,
      expressions: updatedExpressions,
    };
    console.log('[RuleGroup] Calling onUpdate with:', updatedGroup);
    onUpdate(updatedGroup);
  };

  const handleDeleteExpression = (index: number) => {
    const updatedExpressions = expressions.filter((_, i) => i !== index);
    onUpdate({
      ...group,
      expressions: updatedExpressions.length > 0 ? updatedExpressions : null,
    });
  };

  const handleCreateNestedGroup = (index: number) => {
    const currentItem = expressions[index];
    if (!currentItem.expression) return;

    // Convert the current expression into a nested group
    const nestedGroup: ExpressionItem = {
      expression: null,
      expressionGroup: {
        expressions: [
          {
            expression: currentItem.expression,
            expressionGroup: null,
          },
          {
            expression: null,
            expressionGroup: null,
          },
        ],
        logicalOperator: group.logicalOperator === LogicalOperator.And ? LogicalOperator.Or : LogicalOperator.And,
        isNot: false,
      },
    };

    const updatedExpressions = [...expressions];
    updatedExpressions[index] = nestedGroup;

    onUpdate({
      ...group,
      expressions: updatedExpressions,
    });
  };

  const handleAddRule = () => {
    const newItem: ExpressionItem = {
      expression: null,
      expressionGroup: null,
    };

    onUpdate({
      ...group,
      expressions: [...expressions, newItem],
    });
  };

  const getConnectionCellClass = (index: number, total: number) => {
    if (index === 0) return `${styles['connection-cell']} ${styles['connection-cell-first']}`;
    if (index === total - 1) return `${styles['connection-cell']} ${styles['connection-cell-last']}`;
    return `${styles['connection-cell']} ${styles['connection-cell-middle']}`;
  };

  const getExpressionCellClass = (index: number, total: number) => {
    if (index === 0) return `${styles['expression-cell']} ${styles['expression-cell-first']}`;
    if (index === total - 1) return `${styles['expression-cell']} ${styles['expression-cell-last']}`;
    return `${styles['expression-cell']} ${styles['expression-cell-middle']}`;
  };

  return (
    <div className="space-y-3">

      {!hasMultipleExpressions && (
        <div className={styles['single-column']}>
          {expressions.length === 0 ? (
            <SimpleExpressionForm
              initialValue={undefined}
              onCancel={() => {}}
              onChange={(expr) => handleUpdateExpression(0, expr)}
              showDelete={false}
              onAddGroup={() => {}}
              showAddGroup={false}
              fieldMetadata={fieldMetadata}
              operators={operators}
            />
          ) : (
            expressions.map((item, index) =>
              item.expressionGroup ? (
                <div key={index} className={styles['expression-group']}>
                  <RuleGroup
                    group={item.expressionGroup}
                    level={level + 1}
                    onUpdate={(updatedGroup) => {
                      const updatedExpressions = [...expressions];

                      if (updatedGroup.expressions && updatedGroup.expressions.length === 1) {
                        updatedExpressions[index] = updatedGroup.expressions[0];
                      } else {
                        updatedExpressions[index] = {
                          ...updatedExpressions[index],
                          expressionGroup: updatedGroup,
                        };
                      }

                      onUpdate({
                        ...group,
                        expressions: updatedExpressions,
                      });
                    }}
                    fieldMetadata={fieldMetadata}
                    operators={operators}
                  />
                </div>
              ) : (
                <SimpleExpressionForm
                  key={index}
                  initialValue={item.expression || undefined}
                  onCancel={() => handleDeleteExpression(index)}
                  onChange={(expr) => handleUpdateExpression(index, expr)}
                  showDelete={expressions.length > 1}
                  onAddGroup={() => handleCreateNestedGroup(index)}
                  showAddGroup={hasMultipleExpressions && !item.expressionGroup}
                  fieldMetadata={fieldMetadata}
                  operators={operators}
                />
              )
            )
          )}
        </div>
      )}

      {hasMultipleExpressions && (
        <div className={styles.grid}>
          {expressions.map((item, index) => (
            <>
              <div key={`connection-${index}`} className={getConnectionCellClass(index, expressions.length)}>
                {index === 0 && (
                  <div className={styles['logical-operator']}>
                    <LogicalOperatorToggle value={group.logicalOperator} onChange={handleLogicalOperatorChange} />
                  </div>
                )}
              </div>

              <div key={`expression-${index}`} className={getExpressionCellClass(index, expressions.length)}>
                {item.expressionGroup ? (
                  <div className={styles['expression-group']}>
                    <RuleGroup
                      group={item.expressionGroup}
                      level={level + 1}
                      onUpdate={(updatedGroup) => {
                        const updatedExpressions = [...expressions];

                        if (updatedGroup.expressions && updatedGroup.expressions.length === 1) {
                          updatedExpressions[index] = updatedGroup.expressions[0];
                        } else {
                          updatedExpressions[index] = {
                            ...updatedExpressions[index],
                            expressionGroup: updatedGroup,
                          };
                        }

                        onUpdate({
                          ...group,
                          expressions: updatedExpressions,
                        });
                      }}
                      fieldMetadata={fieldMetadata}
                      operators={operators}
                    />
                  </div>
                ) : (
                  <SimpleExpressionForm
                    initialValue={item.expression || undefined}
                    onCancel={() => handleDeleteExpression(index)}
                    onChange={(expr) => handleUpdateExpression(index, expr)}
                    showDelete={expressions.length > 1}
                    onAddGroup={() => handleCreateNestedGroup(index)}
                    showAddGroup={hasMultipleExpressions && !item.expressionGroup}
                    fieldMetadata={fieldMetadata}
                    operators={operators}
                  />
                )}
              </div>
            </>
          ))}
        </div>
      )}

      <Button
        label="Add Rule"
        icon="pi pi-plus"
        link
        size="small"
        onClick={handleAddRule}
        disabled={hasIncompleteExpression}
        className="font-bold mt-2"
        style={{ width: 'auto', color: hasIncompleteExpression ? '#9ca3af' : undefined }}
      />
    </div>
  );
};
